/**
 * Anomalies affectation plan de charge : ce qui empêchera les saisies de tomber juste.
 *
 * *Demande de Chris, 16/09/2026 : « il faut un tableau de contrôle des affectations avec une
 * analyse des anomalies […] accessible pour les admin et responsable d'exploitation dans
 * tableaux de bord […] toutes les anomalies doivent être corrigées au moins deux jours avant la
 * date du jour de saisie sinon les remontées dans les applications ne seront pas bonnes. »*
 *
 * ## Ce qui distingue cet écran du plan de charge
 *
 * Le plan de charge répond « qu'est-ce qu'il me reste à affecter ». Celui-ci répond « qu'est-ce
 * qui empêchera quelqu'un de saisir sa journée », ce qui n'est pas la même question : une
 * personne parfaitement affectée mais sans compte est invisible au plan de charge et bloquante
 * ici. C'est pourquoi le tableau est trié **par échéance** et non par chantier — on l'ouvre pour
 * savoir ce qu'il faut corriger aujourd'hui, pas pour faire le tour des affaires.
 *
 * ## Le délai de deux jours se lit, il ne se devine pas
 *
 * Chaque ligne porte ses jours ouvrés restants, et la couleur en découle. Un tableau qui ne
 * dirait que « anomalie » obligerait à comparer mentalement chaque date au calendrier — et c'est
 * exactement là que le week-end fait perdre deux jours sans qu'on s'en aperçoive.
 *
 * ## Un tableau vide se dit en toutes lettres
 *
 * *Un écran d'alerte qui n'affiche rien se lit « tout va bien », et c'est son pire mode de
 * panne.* Deux cas se distinguent donc : rien à signaler, et **les absences n'ont pas pu être
 * cherchées** parce que le référentiel maître n'a pas répondu — le second n'est pas le premier.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import type { JSX } from "react";
import { formaterDate } from "@planif/moteur";
import { chargerAnomalies } from "./api";
import type { Anomalie, GraviteAnomalie, LectureAnomalies, SorteAnomalie } from "./api";


/**
 * Le nom de chaque sorte, dans la colonne qui les range.
 *
 * Des phrases courtes et non des codes : le tableau se filtre dessus, et « noeud_sans_saisisseur »
 * ne se cherche pas. L'ordre est celui du serveur — la gravité de principe.
 */
const NOMS: Record<SorteAnomalie, string> = {
  noeud_sans_saisisseur: "Personne pour saisir",
  personne_sans_compte: "Sans compte",
  affecte_absent: "Affecté mais absent",
  besoin_sans_ressource: "Besoin non affecté",
  ressource_sur_deux_chantiers: "Sur deux chantiers",
  livraison_sans_vehicule: "Rien pour le charger",
  chantier_sans_date: "Chantier sans date",
};

const LIBELLES_GRAVITE: Record<GraviteAnomalie, string> = {
  depassee: "Délai dépassé",
  critique: "Moins de 2 jours",
  a_corriger: "À corriger",
};

/**
 * Le délai en toutes lettres.
 *
 * « J-1 » et « dans 1 jour ouvré » ne se lisent pas pareil quand on organise une semaine : la
 * phrase dit le sens du temps, le nombre seul demande de le reconstituer.
 */
function direLeDelai(anomalie: Anomalie): string {
  const jours = anomalie.joursOuvresRestants;
  if (jours === null) return "sans échéance";
  if (jours === 0) return "aujourd'hui";
  if (jours < 0) {
    const passes = -jours;
    return `il y a ${String(passes)} jour${passes > 1 ? "s" : ""} ouvré${passes > 1 ? "s" : ""}`;
  }
  return `dans ${String(jours)} jour${jours > 1 ? "s" : ""} ouvré${jours > 1 ? "s" : ""}`;
}

/* ---------------------------------------------------------------------------
 * L'écran
 * ------------------------------------------------------------------------- */

export function AnomaliesAffectation(): JSX.Element {
  const [lecture, setLecture] = useState<LectureAnomalies | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(true);
  const [debut, setDebut] = useState("");
  const [fin, setFin] = useState("");
  /** Le filtre par sorte : `null` = tout. Une seule à la fois, c'est une vue de travail. */
  const [sorte, setSorte] = useState<SorteAnomalie | null>(null);
  /** Masquer ce qui est encore loin, pour ne garder que ce qui presse. */
  const [urgentesSeulement, setUrgentesSeulement] = useState(false);

  const charger = useCallback(async (bornes?: { debut?: string; fin?: string }) => {
    setChargement(true);
    setErreur(null);
    try {
      const lu = await chargerAnomalies(bornes);
      setLecture(lu);
      setDebut(lu.debut);
      setFin(lu.fin);
    } catch (cause) {
      setErreur(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    void charger();
  }, [charger]);

  const anomalies = lecture?.anomalies ?? [];

  /** Le décompte par gravité porte sur TOUT, jamais sur ce que le filtre laisse voir. */
  const compte = useMemo(() => {
    const par: Record<GraviteAnomalie, number> = { depassee: 0, critique: 0, a_corriger: 0 };
    for (const a of anomalies) par[a.gravite] += 1;
    return par;
  }, [anomalies]);

  /** Combien de chaque sorte — c'est ce qui fait des boutons de filtre utiles. */
  const parSorte = useMemo(() => {
    const par = new Map<SorteAnomalie, number>();
    for (const a of anomalies) par.set(a.sorte, (par.get(a.sorte) ?? 0) + 1);
    return par;
  }, [anomalies]);

  const visibles = useMemo(
    () =>
      anomalies.filter((a) => {
        if (sorte !== null && a.sorte !== sorte) return false;
        if (urgentesSeulement && a.gravite === "a_corriger") return false;
        return true;
      }),
    [anomalies, sorte, urgentesSeulement],
  );

  return (
    <section className="anomalies">
      <p className="explication">
        Ce qui empêchera les saisies de chantier de remonter juste. Une anomalie se corrige{" "}
        <strong>au moins deux jours ouvrés avant le jour qu'elle concerne</strong> : passé ce
        délai, la saisie du soir part sur un plan faux, et c'est le réel qui devient faux.
      </p>

      <form
        className="anomalies__periode"
        onSubmit={(evenement) => {
          evenement.preventDefault();
          void charger({ debut, fin });
        }}
      >
        <label>
          Du
          <input
            type="date"
            value={debut}
            onChange={(e) => {
              setDebut(e.target.value);
            }}
          />
        </label>
        <label>
          au
          <input
            type="date"
            value={fin}
            onChange={(e) => {
              setFin(e.target.value);
            }}
          />
        </label>
        <button type="submit" disabled={chargement}>
          Afficher
        </button>
      </form>

      {/* Les compteurs portent sur toute la période, jamais sur le filtre : un filtre qui
          ferait aussi bouger le total ferait croire qu'on a corrigé quelque chose. */}
      <div className="anomalies__compteurs">
        {(["depassee", "critique", "a_corriger"] as const).map((gravite) => (
          <span key={gravite} className={`anomalies__compteur anomalies__compteur--${gravite}`}>
            <strong>{compte[gravite]}</strong> {LIBELLES_GRAVITE[gravite].toLowerCase()}
          </span>
        ))}
      </div>

      <div className="anomalies__filtres">
        <button
          type="button"
          className={sorte === null ? "actif" : undefined}
          onClick={() => {
            setSorte(null);
          }}
        >
          Tout ({anomalies.length})
        </button>
        {(Object.keys(NOMS) as SorteAnomalie[])
          .filter((cle) => (parSorte.get(cle) ?? 0) > 0)
          .map((cle) => (
            <button
              key={cle}
              type="button"
              className={sorte === cle ? "actif" : undefined}
              onClick={() => {
                setSorte(sorte === cle ? null : cle);
              }}
            >
              {NOMS[cle]} ({parSorte.get(cle) ?? 0})
            </button>
          ))}
        <label className="anomalies__bascule">
          <input
            type="checkbox"
            checked={urgentesSeulement}
            onChange={(e) => {
              setUrgentesSeulement(e.target.checked);
            }}
          />
          Hors délai seulement
        </label>
      </div>

      {erreur !== null && <p className="erreur">{erreur}</p>}

      {/*
        Le référentiel muet se dit, et ne se confond pas avec « aucun absent affecté ».
        C'est la règle du compteur qui traverse toujours : un contrôle qui se tait faute de
        données ressemble trait pour trait à un contrôle qui ne trouve rien.
      */}
      {lecture !== null && !lecture.absencesConnues && (
        <p className="alerte">
          Le référentiel des absences n'a pas répondu : les affectations tombant un jour de congé
          ou d'arrêt <strong>n'ont pas pu être cherchées</strong>. Les autres contrôles sont
          complets.
        </p>
      )}
      {lecture !== null && lecture.absencesSansSalarie > 0 && (
        <p className="alerte">
          {lecture.absencesSansSalarie} absence(s) du référentiel ne sont rattachées à aucune fiche
          salarié : elles sont invisibles ici comme partout. À corriger dans le référentiel des
          salariés.
        </p>
      )}

      {chargement && <p>Lecture du plan de charge…</p>}

      {!chargement && lecture !== null && anomalies.length === 0 && (
        <p className="anomalies__vide">
          Aucune anomalie du {formaterDate(lecture.debut)} au {formaterDate(lecture.fin)} : les
          affectations de la période permettent la saisie.
        </p>
      )}

      {visibles.length > 0 && (
        <div className="tableau-defilant">
          <table className="anomalies__table">
            <thead>
              <tr>
                <th>Échéance</th>
                <th>Anomalie</th>
                <th>Affaire</th>
                <th>Nœud</th>
                <th>Ce qui cloche</th>
                <th>Où corriger</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((anomalie, rang) => (
                <tr
                  key={`${anomalie.sorte}#${anomalie.chantierId}#${anomalie.ouvrage ?? ""}#${anomalie.date ?? ""}#${String(rang)}`}
                  className={`anomalies__ligne anomalies__ligne--${anomalie.gravite}`}
                >
                  {/* `data-colonne` n'est lu que sous 900 px, où le tableau devient des cartes :
                      six valeurs empilées sans leur en-tête ne se rattachent plus à rien. */}
                  <td className="anomalies__echeance" data-colonne="Échéance">
                    {anomalie.date === null ? (
                      <span className="anomalies__sans-date">sans échéance</span>
                    ) : (
                      <>
                        <span className="anomalies__jour">{formaterDate(anomalie.date)}</span>
                        <span className="anomalies__delai">{direLeDelai(anomalie)}</span>
                      </>
                    )}
                  </td>
                  <td>
                    <span className={`etiquette etiquette--${anomalie.gravite}`}>
                      {NOMS[anomalie.sorte]}
                    </span>
                  </td>
                  <td data-colonne="Affaire">
                    <span className="anomalies__apo">{anomalie.apo}</span>
                    <span className="anomalies__nom">{anomalie.nomChantier}</span>
                  </td>
                  <td data-colonne="Nœud">
                    {anomalie.ouvrage === null ? (
                      <span className="anomalies__sans-date">—</span>
                    ) : (
                      <span title={anomalie.ouvrageLibelle ?? undefined}>{anomalie.ouvrage}</span>
                    )}
                  </td>
                  <td data-colonne="Ce qui cloche">{anomalie.quoi}</td>
                  <td className="anomalies__correction" data-colonne="Où corriger">
                    {anomalie.correction}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!chargement && anomalies.length > 0 && visibles.length === 0 && (
        <p className="anomalies__vide">
          Rien sous ce filtre. {anomalies.length} anomalie(s) sur la période.
        </p>
      )}
    </section>
  );
}
